import { useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Menu,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useQuery } from 'react-query';
import { toast } from 'react-toastify';
import CustomerInfobar from '../../../customerInfobar';
import Can from 'components/auth/Can';
import { useCan } from 'hooks/useCan';
import { usePreferences } from 'hooks/usePreferences';
import { usePrint } from '../../../../../hooks/usePrint';
import useInvoiceCalculations from '../../../../../hooks/useInvoiceCalculations';
import { formatAmountForDisplayWithPreferences } from '../../../../../utils';
import { formatDateWithPreferences } from '../../../../../utils/dateFormatUtils';
import { InvoiceService } from '../../../../../service/invoiceService';
import TemplatePrintButton from '../../../../print/TemplatePrintButton';
import PrintPreviewDialog from '../../../../print/PrintPreviewDialog';
import SendInvoiceEmailDialog from './SendInvoiceEmailDialog';

export default function ReturnInvoiceDialog({ onClose, open, customer, invoiceData }) {
    const can = useCan();
    const { preferences } = usePreferences();
    const [sendEmailDialog, setSendEmailDialog] = useState({ open: false, invoice: null });
    const [printMenuAnchorEl, setPrintMenuAnchorEl] = useState(null);
    const customerRecord = customer?.customer || null;

    const { printPDF, previewUrl, closePreview, isGenerating: isPrinting, cleanup } = usePrint();
    const invoicePrintRef = useRef();

    useEffect(() => {
        if (!open) {
            cleanup();
        }
    }, [open, cleanup]);

    const { data: invoiceItemsResponse } = useQuery(
        ['return-invoice-items', invoiceData?.id],
        () => InvoiceService.getInvoiceItems(invoiceData.id),
        { enabled: open && !!invoiceData?.id }
    );

    const hydratedInvoice = useMemo(() => invoiceItemsResponse?.data || {}, [invoiceItemsResponse]);
    const activeInvoice = useMemo(() => ({
        id: hydratedInvoice?.id || invoiceData?.id,
        invoiceNumber: hydratedInvoice?.invoiceNumber || invoiceData?.invoiceNumber,
        sourceInvoiceId: hydratedInvoice?.sourceInvoiceId || invoiceData?.sourceInvoiceId,
        status: hydratedInvoice?.status || invoiceData?.status,
        returnReason: hydratedInvoice?.returnReason || invoiceData?.returnReason,
        tray: hydratedInvoice?.tray || invoiceData?.tray,
        associate: hydratedInvoice?.associate || invoiceData?.associate,
        invoiceDate: hydratedInvoice?.invoiceDate || invoiceData?.invoiceDate,
        items: hydratedInvoice?.items || [],
        payments: hydratedInvoice?.payments || [],
        discounts: hydratedInvoice?.discounts || [],
        insuranceClaims: hydratedInvoice?.insuranceClaims || [],
        refunds: hydratedInvoice?.refunds || [],
        notes: hydratedInvoice?.notes || invoiceData?.notes,
    }), [hydratedInvoice, invoiceData]);

    const sourceHydratedInvoice = useMemo(() => hydratedInvoice?.sourceInvoiceDetails || {}, [hydratedInvoice]);
    const sourceInvoiceId = hydratedInvoice?.sourceInvoiceId || invoiceData?.sourceInvoiceId;
    const sourceInvoice = useMemo(() => ({
        id: sourceHydratedInvoice?.id || sourceInvoiceId,
        invoiceNumber: sourceHydratedInvoice?.invoiceNumber || null,
        status: sourceHydratedInvoice?.status || 'open',
        invoiceDate: sourceHydratedInvoice?.invoiceDate || null,
        items: sourceHydratedInvoice?.items || [],
        payments: sourceHydratedInvoice?.payments || [],
        discounts: sourceHydratedInvoice?.discounts || [],
        insuranceClaims: sourceHydratedInvoice?.insuranceClaims || [],
        refunds: sourceHydratedInvoice?.refunds || [],
    }), [sourceHydratedInvoice, sourceInvoiceId]);

    const approvedBy = useMemo(() => {
        const withApprover = (sourceInvoice.refunds || []).filter(refund => refund?.authorizedBy);
        if (withApprover.length === 0) return null;

        const sorted = [...withApprover].sort((a, b) => {
            const aTs = new Date(a?.processedDate || a?.refundDate || 0).getTime();
            const bTs = new Date(b?.processedDate || b?.refundDate || 0).getTime();
            return bTs - aTs;
        });

        return sorted[0]?.authorizedBy || null;
    }, [sourceInvoice.refunds]);

    const calculations = useInvoiceCalculations({
        status: 'refund',
        invoiceItems: activeInvoice.items,
        payments: activeInvoice.payments,
        discounts: activeInvoice.discounts,
        claims: activeInvoice.insuranceClaims,
        refunds: activeInvoice.refunds,
    });

    // const sourceCalculations = useInvoiceCalculations({
    //     status: sourceInvoice.status,
    //     invoiceItems: sourceInvoice.items,
    //     payments: sourceInvoice.payments,
    //     discounts: sourceInvoice.discounts,
    //     claims: sourceInvoice.insuranceClaims,
    //     refunds: sourceInvoice.refunds,
    // });

    const canSendEmailNow = can.action('invoice', 'send-email')
        && Boolean(customerRecord?.email)
        && Boolean(activeInvoice?.invoiceNumber)
        && Boolean(activeInvoice?.id);

    const withReturnPrefix = (label) => {
        const text = String(label || 'N/A');
        return text.trim().toLowerCase().startsWith('return -') ? text : `Return - ${text}`;
    };

    const withRefundPayoutPrefix = (label) => {
        const text = String(label || 'N/A');
        if (text.trim().toLowerCase().startsWith('refund payout -')) return text;
        return `Refund payout - ${text}`;
    };

    const getReturnPaymentLabel = (payment) => {
        const baseLabel = payment?.name || payment?.paymentMethod?.name || 'N/A';
        const amount = Number(payment?.paymentAmount || 0);
        const notes = String(payment?.notes || '').toLowerCase();
        if (amount < 0) {
            return withRefundPayoutPrefix(baseLabel);
        }
        if (notes.includes('restocking fee collected')) {
            return `Restocking fee payment - ${baseLabel}`;
        }
        return withReturnPrefix(baseLabel);
    };

    const sumBy = (rows = [], field) => rows.reduce((sum, row) => sum + Number(row?.[field] || 0), 0);
    const returnItemsTotal = sumBy(activeInvoice.items, 'total');
    const returnDiscountsTotal = sumBy(activeInvoice.discounts, 'appliedAmount');
    const returnInsuranceTotal = sumBy(activeInvoice.insuranceClaims, 'approvedAmount');
    const sourceRefundsTotal = sumBy(sourceInvoice.refunds, 'refundAmount');
    // For return-invoices, customer collections are usually represented as negative payment rows (refund payouts), but any positive payment rows are real customer payments that should be added back to the total customer paid when calculating net settlement position.
    const returnCustomerCollections = (activeInvoice.payments || []).reduce((sum, payment) => {
        const amount = Number(payment?.paymentAmount || 0);
        return amount > 0 ? sum + amount : sum;
    }, 0);
    // Insurance reversal claims on the return invoice have status 'reversed' and a negative approvedAmount.
    // useInvoiceCalculations only counts 'approved' status, so we must accumulate reversals separately.
    const returnInsuranceReversals = (activeInvoice.insuranceClaims || []).reduce((sum, claim) => {
        if (String(claim?.status || '').toLowerCase() === 'reversed') {
            return sum + Number(claim?.approvedAmount || 0); // already stored as negative
        }
        return sum;
    }, 0);

    //const netTotalDue = sourceCalculations.totalDue + calculations.totalDue;
    // Return-invoice payment rows are usually synthetic reversal entries, but positive
    // rows such as separately collected restocking fees are real customer payments.
    //const netCustomerPaid = sourceCalculations.totalCustomerPaid - sourceRefundsTotal + returnCustomerCollections;
    //const netInsuranceApproved = sourceCalculations.totalInsuranceApproved + calculations.totalInsuranceApproved + returnInsuranceReversals;
    // const combinedNetBalance = netTotalDue - netCustomerPaid - netInsuranceApproved;

    const handlePrintInvoice = async (templateId) => {
        if (!activeInvoice?.id) {
            toast.error('Invoice not ready for printing');
            return;
        }

        try {
            await printPDF(() => InvoiceService.print(activeInvoice.id, templateId));
        } catch (_error) {
            toast.error('Failed to print invoice');
        }
    };

    const handleDeleteInvoice = async (invoiceId) => {
        if (!invoiceId) {
            toast.error('Invoice not ready for deletion');
            return;
        }

        try {
            await InvoiceService.deleteInvoice([invoiceId]);
            toast.success('Invoice deleted successfully');
        } catch (_error) {
            toast.error('Failed to delete invoice');
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>
                Return Invoice {activeInvoice?.invoiceNumber ? `- ${activeInvoice.invoiceNumber}` : ''}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ mt: 1 }}>
                    <CustomerInfobar customerData={customer} />

                    <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
                        This is a read-only return invoice showing only reversed (negative) values that offset the source invoice.
                    </Alert>

                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <Typography variant="body2"><strong>Source Invoice ID:</strong> {activeInvoice.sourceInvoiceId || 'N/A'}</Typography>
                            <Typography variant="body2"><strong>Source Invoice #:</strong> {sourceInvoice.invoiceNumber || 'N/A'}</Typography>
                            <Typography variant="body2"><strong>Status:</strong> {activeInvoice.status || 'refund'}</Typography>
                            <Typography variant="body2"><strong>Invoice Date:</strong> {activeInvoice.invoiceDate ? formatDateWithPreferences(activeInvoice.invoiceDate, preferences) : 'N/A'}</Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="body2"><strong>Tray:</strong> {activeInvoice.tray || 'N/A'}</Typography>
                            <Typography variant="body2"><strong>Associate:</strong> {activeInvoice.associate || 'N/A'}</Typography>
                            <Typography variant="body2"><strong>Reason:</strong> {activeInvoice.returnReason || 'N/A'}</Typography>
                            <Typography variant="body2"><strong>Approved By:</strong> {approvedBy || 'N/A'}</Typography>
                        </Grid>
                    </Grid>

                    <Box sx={{ mt: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            Return Items (Reversed Values)
                        </Typography>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Description</TableCell>
                                        <TableCell>Type</TableCell>
                                        <TableCell align="right">Qty</TableCell>
                                        <TableCell align="right">Total</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {activeInvoice.items.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={4} align="center">No items found.</TableCell>
                                        </TableRow>
                                    ) : (
                                        activeInvoice.items.map(item => (
                                            <TableRow key={`return-item-${item.id}`}>
                                                <TableCell>{item.description || 'N/A'}</TableCell>
                                                <TableCell>{item.type || 'N/A'}</TableCell>
                                                <TableCell align="right">{item.quantity || 0}</TableCell>
                                                <TableCell align="right">{formatAmountForDisplayWithPreferences(item.total || 0, preferences)}</TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>

                    <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            Total Return Amount: {formatAmountForDisplayWithPreferences(returnItemsTotal, preferences)}
                        </Typography>
                    </Box>

                    <Box sx={{ mt: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            Return Payments (Reversed)
                        </Typography>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Method</TableCell>
                                        <TableCell align="right">Amount</TableCell>
                                        <TableCell>Date</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {activeInvoice.payments.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={3} align="center">No payments found.</TableCell>
                                        </TableRow>
                                    ) : (
                                        activeInvoice.payments.map(payment => (
                                            <TableRow key={`return-payment-${payment.id}`}>
                                                <TableCell>{getReturnPaymentLabel(payment)}</TableCell>
                                                <TableCell align="right">{formatAmountForDisplayWithPreferences(payment.paymentAmount || 0, preferences)}</TableCell>
                                                <TableCell>{payment.paymentDate ? formatDateWithPreferences(payment.paymentDate, preferences) : 'N/A'}</TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>

                    <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" color="text.secondary" display="block">
                            Negative payment rows represent refund payouts to the customer. Positive rows represent additional fees collected.
                        </Typography>
                    </Box>

                    <Box sx={{ mt: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            Return Discounts (Reversed)
                        </Typography>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Type</TableCell>
                                        <TableCell align="right">Value</TableCell>
                                        <TableCell align="right">Applied</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {activeInvoice.discounts.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={3} align="center">No discounts found.</TableCell>
                                        </TableRow>
                                    ) : (
                                        activeInvoice.discounts.map(discount => (
                                            <TableRow key={`return-discount-${discount.id}`}>
                                                <TableCell>{discount.type || 'N/A'}</TableCell>
                                                <TableCell align="right">{formatAmountForDisplayWithPreferences(discount.value || 0, preferences)}</TableCell>
                                                <TableCell align="right">{formatAmountForDisplayWithPreferences(discount.appliedAmount || 0, preferences)}</TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>

                    <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            Total Discounts Reversed: {formatAmountForDisplayWithPreferences(returnDiscountsTotal, preferences)}
                        </Typography>
                    </Box>

                    <Box sx={{ mt: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            Return Insurance Claims (Reversed)
                        </Typography>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Provider</TableCell>
                                        <TableCell align="right">Claim</TableCell>
                                        <TableCell align="right">Approved</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {activeInvoice.insuranceClaims.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={3} align="center">No insurance claims found.</TableCell>
                                        </TableRow>
                                    ) : (
                                        activeInvoice.insuranceClaims.map(claim => (
                                            <TableRow key={`return-claim-${claim.id}`}>
                                                <TableCell>{claim.provider || 'N/A'}</TableCell>
                                                <TableCell align="right">{formatAmountForDisplayWithPreferences(claim.claimAmount || 0, preferences)}</TableCell>
                                                <TableCell align="right">{formatAmountForDisplayWithPreferences(claim.approvedAmount || 0, preferences)}</TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>

                    <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            Total Insurance Reversed: {formatAmountForDisplayWithPreferences(returnInsuranceTotal, preferences)}
                        </Typography>
                    </Box>

                    <Box sx={{ mt: 2, p: 1.5, border: '1px solid #ccc', borderRadius: 1, backgroundColor: '#f9f9f9' }}>
                        {/* <Typography variant="h6" gutterBottom>Return Invoice Summary</Typography> */}
                        {/* <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography>Total Return (Reversed):</Typography>
                            <Typography>{formatAmountForDisplayWithPreferences(calculations.totalDue, preferences)}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography>Return Payments Applied:</Typography>
                            <Typography>{formatAmountForDisplayWithPreferences(calculations.totalCustomerPaid, preferences)}</Typography>
                        </Box> */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                            <Typography>Return Balance:</Typography>
                            <Typography>{formatAmountForDisplayWithPreferences(calculations.balanceDue, preferences)}</Typography>
                        </Box>
                        {/* <Box sx={{ borderTop: '1px solid #ccc', pt: 1, mb: 0.5 }} /> */}
                        {/* <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="body2" color="text.secondary">Combined Source Balance:</Typography>
                            <Typography variant="body2" color="text.secondary">{formatAmountForDisplayWithPreferences(sourceCalculations.balanceDue, preferences)}</Typography>
                        </Box> */}
                        {/* <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                Net Settlement Position:
                            </Typography>
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 'bold',
                                    color: combinedNetBalance < -0.000001 ? 'success.main' : (combinedNetBalance > 0.000001 ? 'error.main' : 'text.primary')
                                }}
                            >
                                {formatAmountForDisplayWithPreferences(combinedNetBalance, preferences)}
                            </Typography>
                        </Box> */}
                    </Box>

                    {activeInvoice.notes && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2">Notes</Typography>
                            <Typography variant="body2" color="text.secondary">{activeInvoice.notes}</Typography>
                        </Box>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Can section='invoice' action='print-invoice'>
                    <Button
                        color="secondary"
                        variant="outlined"
                        startIcon={<PrintIcon />}
                        endIcon={<ArrowDropDownIcon />}
                        onClick={(e) => setPrintMenuAnchorEl(e.currentTarget)}
                        disabled={!activeInvoice.invoiceNumber || isPrinting}
                    >
                        Print
                    </Button>
                    <Menu
                        anchorEl={printMenuAnchorEl}
                        open={Boolean(printMenuAnchorEl)}
                        onClose={() => setPrintMenuAnchorEl(null)}
                    >
                        <MenuItem disableRipple sx={{ p: 0 }}>
                            <TemplatePrintButton
                                ref={invoicePrintRef}
                                documentType="invoice"
                                onPrint={async (templateId) => {
                                    setPrintMenuAnchorEl(null);
                                    await handlePrintInvoice(templateId);
                                }}
                                disabled={!activeInvoice.invoiceNumber || isPrinting}
                                buttonProps={{ variant: 'text', color: 'inherit', fullWidth: true, sx: { justifyContent: 'flex-start', px: 2, py: 1 } }}
                            >
                                Customer Invoice
                            </TemplatePrintButton>
                        </MenuItem>
                    </Menu>
                </Can>

                {can.action('invoice', 'send-email') && (
                    <Button
                        color="info"
                        variant="outlined"
                        onClick={() => {
                            setSendEmailDialog({
                                open: true,
                                invoice: {
                                    id: activeInvoice.id,
                                    invoiceNumber: activeInvoice.invoiceNumber,
                                }
                            });
                        }}
                        disabled={!canSendEmailNow}
                    >
                        Send Email
                    </Button>
                )}
                {/* allow for now */}
                {can.action('invoice','delete-invoice') && (
                    <Button
                        color="error"
                        variant="outlined"
                        onClick={() => handleDeleteInvoice(activeInvoice.id)}
                    >
                        Delete
                    </Button>
                )}

                <Button color="cancel" variant="outlined" onClick={onClose}>Close</Button>
            </DialogActions>

            <SendInvoiceEmailDialog
                open={sendEmailDialog.open}
                recipient={sendEmailDialog.invoice ? {
                    recipientId: customerRecord?.id,
                    email: customerRecord?.email,
                    name: customerRecord?.name || `${customerRecord?.firstName || ''} ${customerRecord?.lastName || ''}`.trim(),
                } : null}
                entityId={sendEmailDialog.invoice?.id}
                invoiceNumber={sendEmailDialog.invoice?.invoiceNumber}
                onClose={() => setSendEmailDialog({ open: false, invoice: null })}
                onSuccess={(message) => {
                    toast.success(message);
                    setSendEmailDialog({ open: false, invoice: null });
                }}
            />

            <PrintPreviewDialog url={previewUrl} onClose={closePreview} />
        </Dialog>
    );
}
